/* =====================================================================
   TAB Calculator Library — shared engine (V2)
   Renders the approved V2 calculator layout from a per-page config
   (registered via TABCalc.register). One engine for Beer/Wine/Spirits;
   each page loads only its own data file. Ports calc-format.js (units)
   and reproduces the approved CalculatorCard interaction natively —
   no prototype runtime, custom elements or dynamic imports.
   ===================================================================== */
(function () {
  'use strict';

  var DISCLAIMER = [
    'These calculators provide estimates only and work from the numbers entered by you. A warm hydrometer sample, an approximate batch volume or an enthusiastic scoop of sugar can all change the final result.',
    'Please check your measurements, follow the instructions for your ingredients and equipment, and use your own judgement. The Artisans Bottega cannot take responsibility for your batch, your bottles, your carbonation level or the unexpected personality of the beverage that eventually turns up in your glass.',
    'Enjoy making, enjoy sharing and drink responsibly.'
  ];

  var ICON = {
    chevron: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2"><path d="m6 9 6 6 6-6"/></svg>',
    need: '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.7"><path d="m9 11 3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>',
    calc: '<svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="1.9"><rect x="4" y="2" width="16" height="20" rx="2"/><path d="M8 6h8"/><path d="M8 10h.01M12 10h.01M16 10h.01M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01M16 18h.01"/></svg>',
    copy: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.7"><rect x="9" y="9" width="12" height="12" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>',
    info: '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.7"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>',
    warn: '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M12 9v4"/><path d="M12 17h.01"/><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/></svg>',
    error: '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="12" cy="12" r="10"/><path d="M12 8v5"/><path d="M12 16h.01"/></svg>',
    arrow: '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>'
  };

  var registry = {};

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function el(tag, cls, html) { var n = document.createElement(tag); if (cls) n.className = cls; if (html != null) n.innerHTML = html; return n; }

  /* ---- units (ported from calc-format.js) ---- */
  function makeUnits(system) {
    var GAL = 3.785411784;
    var metric = system !== 'imperial';
    function round(n, dp) { if (!isFinite(n)) return 0; var f = Math.pow(10, dp == null ? 2 : dp); return Math.round(n * f) / f; }
    function tidy(n, dp) { return String(round(n, dp)); }
    return {
      system: system, metric: metric,
      volU: metric ? 'L' : 'gal', tempU: metric ? '°C' : '°F', ratePerU: metric ? 'L/hr' : 'gal/hr',
      toL: function (v) { return metric ? +v : +v * GAL; },
      toC: function (v) { return metric ? +v : (+v - 32) * (5 / 9); },
      vol: function (litres) { return metric ? { value: tidy(litres, 2), unit: 'L' } : { value: tidy(litres / GAL, 2), unit: 'gal' }; },
      wt: function (grams) { if (grams >= 1000) return { value: tidy(grams / 1000, 2), unit: 'kg' }; if (grams >= 10) return { value: tidy(grams, 0), unit: 'g' }; return { value: tidy(grams, 1), unit: 'g' }; }
    };
  }

  function track(name, params) {
    try {
      if (window.dataLayer) window.dataLayer.push(Object.assign({ event: name }, params || {}));
      if (window.gtag) window.gtag('event', name, params || {});
    } catch (e) {}
  }

  /* =================================================================
     Instance
     ================================================================= */
  function Inst(root, cfg) {
    this.root = root;
    this.cfg = cfg;
    this.units = cfg.units || 'metric';
    try { var u = localStorage.getItem('tab_units'); if (u) this.units = u; } catch (e) {}
    this.cards = {};     // id -> { calc, index, cardEl, state, els }
    this.openId = null;
  }

  Inst.prototype.init = function () {
    // continuous numbering across groups (group order)
    var n = 0, self = this;
    this.ordered = [];
    this.cfg.groups.forEach(function (g) {
      self.cfg.calculators.filter(function (c) { return c.group === g.id; }).forEach(function (c) {
        self.ordered.push({ calc: c, group: g, index: ++n });
      });
    });
    this.renderChips();
    this.renderGroups();
    this.renderFooter();
    this.bindUnitToggle();
    this.updateCount();
    this.measureOffset();
    window.addEventListener('resize', function () { self.measureOffset(); });
    window.addEventListener('load', function () { self.measureOffset(); });
    // default open: hash #card-<id> or first calc of first group
    var openId = this.ordered.length ? this.ordered[0].calc.id : null;
    var hashId = this.hashId();
    if (hashId) openId = hashId;
    if (openId) this.openCard(openId, false);
    window.addEventListener('hashchange', function () { var id = self.hashId(); if (id) self.openCard(id, true); });
  };

  Inst.prototype.hashId = function () {
    var h = (location.hash || '').replace('#', '');
    if (h.indexOf('card-') === 0) {
      var id = h.slice(5);
      if (this.cfg.calculators.some(function (c) { return c.id === id; })) return id;
    }
    return null;
  };

  Inst.prototype.updateCount = function () {
    var c = this.root.querySelector('[data-tabcalc-count]');
    if (c) c.textContent = this.cfg.calculators.length + ' calculators';
  };

  /* ---- units toggle ---- */
  Inst.prototype.bindUnitToggle = function () {
    var self = this;
    this.root.querySelectorAll('.tabcalc-seg-btn').forEach(function (b) {
      b.setAttribute('aria-pressed', b.dataset.units === self.units ? 'true' : 'false');
      b.classList.toggle('is-active', b.dataset.units === self.units);
      b.addEventListener('click', function () { self.setUnits(b.dataset.units); });
    });
  };
  Inst.prototype.setUnits = function (u) {
    if (u === this.units) return;
    this.units = u;
    try { localStorage.setItem('tab_units', u); } catch (e) {}
    this.root.querySelectorAll('.tabcalc-seg-btn').forEach(function (b) {
      var on = b.dataset.units === u;
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
      b.classList.toggle('is-active', on);
    });
    if (this.openId) this.buildBody(this.openId);   // re-render open card in new units
    track('measurement_preference_changed', { measurement_mode: u });
  };

  /* ---- sticky offset (below theme header) ---- */
  Inst.prototype.measureOffset = function () {
    var sels = ['sticky-header', '.header-wrapper', '.shopify-section-group-header-group', '#shopify-section-sections--header', '.section-header', '.header-sticky', '[data-sticky-header]', '.site-header', '#header'];
    var max = 0, vw = window.innerWidth;
    for (var i = 0; i < sels.length; i++) {
      var nodes = document.querySelectorAll(sels[i]);
      for (var j = 0; j < nodes.length; j++) {
        var pos = getComputedStyle(nodes[j]).position;
        if (pos !== 'fixed' && pos !== 'sticky') continue;
        var r = nodes[j].getBoundingClientRect();
        if (r.top <= 4 && r.bottom > max && r.width > vw * 0.5) max = r.bottom;
      }
    }
    this.headerOffset = max;
    this.root.style.setProperty('--tabcalc-header-offset', Math.round(max) + 'px');
  };
  Inst.prototype.scrollOffset = function () {
    var sub = this.root.querySelector('.tabcalc-sub');
    var subH = sub ? sub.getBoundingClientRect().height : 0;
    return (this.headerOffset || 0) + subH + 16;
  };

  /* ---- quick-nav chips ---- */
  Inst.prototype.renderChips = function () {
    var host = this.root.querySelector('#tabcalc-chips');
    if (!host) return;
    var self = this;
    (this.cfg.chips || []).forEach(function (ch) {
      var a = el('a', 'tabcalc-chip'); a.href = '#card-' + ch.id; a.textContent = ch.label;
      a.addEventListener('click', function (e) { e.preventDefault(); self.openCard(ch.id, true); });
      host.appendChild(a);
    });
  };

  /* ---- footer (responsible note + related) ---- */
  Inst.prototype.renderFooter = function () {
    var f = this.cfg.footer || {};
    var r = this.root.querySelector('[data-tabcalc-responsible]');
    if (r && f.responsible) r.textContent = f.responsible;
    var rel = this.root.querySelector('#tabcalc-related');
    if (rel && f.related) {
      f.related.forEach(function (x) {
        var a = el('a', 'tabcalc-related-link', esc(x.label) + ICON.arrow);
        a.href = x.href || '#';
        rel.appendChild(a);
      });
    }
  };

  /* ---- groups + cards ---- */
  Inst.prototype.renderGroups = function () {
    var mount = this.root.querySelector('#tabcalc-mount');
    if (!mount) return;
    var self = this;
    this.cfg.groups.forEach(function (g) {
      var items = self.ordered.filter(function (o) { return o.group.id === g.id; });
      if (!items.length) return;
      var sec = el('div', 'tabcalc-group');
      var head = el('div', 'tabcalc-group-head');
      head.id = g.anchor;
      head.innerHTML = '<h2>' + esc(g.title) + '</h2><span class="tabcalc-rule"></span>' +
        '<span class="tabcalc-group-count">' + items.length + (items.length === 1 ? ' tool' : ' tools') + '</span>';
      sec.appendChild(head);
      var list = el('div', 'tabcalc-list');
      items.forEach(function (o) { list.appendChild(self.buildCard(o)); });
      sec.appendChild(list);
      if (g.learn && g.learn.length) sec.appendChild(self.buildLearn(g.learn));
      mount.appendChild(sec);
    });
  };

  Inst.prototype.buildLearn = function (learn) {
    var box = el('div', 'tabcalc-learn');
    var h = '<div class="tabcalc-learn-head">' + ICON.need.replace('stroke-width="1.7"', 'stroke-width="1.7" stroke="var(--tabcalc-red)"') + '<span>Keep learning</span></div><div class="tabcalc-learn-grid"></div>';
    box.innerHTML = h;
    var grid = box.querySelector('.tabcalc-learn-grid');
    learn.forEach(function (L) {
      var a = el('a', 'tabcalc-learn-card');
      a.href = L.href || '#';
      a.innerHTML = '<span class="tabcalc-learn-kicker">' + esc(L.kicker || 'Guide') + '</span>' +
        '<span class="tabcalc-learn-title">' + esc(L.title) + '</span>' +
        '<span class="tabcalc-learn-more">Read guide' + ICON.arrow + '</span>';
      grid.appendChild(a);
    });
    return box;
  };

  Inst.prototype.buildCard = function (o) {
    var self = this, c = o.calc;
    var card = el('div', 'tabcalc-card');
    card.id = 'card-' + c.id;
    card.dataset.calc = c.id;
    var badge = String(o.index).padStart(2, '0');
    var tag = c.tag ? '<span class="tabcalc-tag ' + (c.tag === 'Start here' || c.tag === 'Most used' ? 'tabcalc-tag--red' : '') + '">' + esc(c.tag) + '</span>' : '';
    var head = el('button', 'tabcalc-card-head');
    head.type = 'button';
    head.setAttribute('aria-expanded', 'false');
    head.setAttribute('aria-controls', 'body-' + c.id);
    head.innerHTML =
      '<span class="tabcalc-num">' + badge + '</span>' +
      '<span class="tabcalc-card-headmain"><span class="tabcalc-card-titlerow"><span class="tabcalc-card-title">' + esc(c.name) + '</span>' + tag + '</span>' +
      '<span class="tabcalc-card-desc">' + esc(c.desc) + '</span></span>' +
      '<span class="tabcalc-chev">' + ICON.chevron + '</span>';
    head.addEventListener('click', function () { self.toggle(c.id); });
    var body = el('div', 'tabcalc-card-body'); body.id = 'body-' + c.id;
    card.appendChild(head); card.appendChild(body);
    this.cards[c.id] = { calc: c, index: o.index, cardEl: card, head: head, body: body,
      state: { vals: null, calculated: true, copied: false } };
    return card;
  };

  /* ---- open / close (one at a time) ---- */
  Inst.prototype.toggle = function (id) { if (this.openId === id) this.closeCard(id); else this.openCard(id, true); };
  Inst.prototype.openCard = function (id, scroll) {
    var rec = this.cards[id]; if (!rec) return;
    if (this.openId && this.openId !== id) this.closeCard(this.openId);
    rec.cardEl.classList.add('is-open');
    rec.head.setAttribute('aria-expanded', 'true');
    this.openId = id;
    this.buildBody(id);
    try { history.replaceState(null, '', '#card-' + id); } catch (e) {}
    if (scroll) {
      var self = this;
      requestAnimationFrame(function () {
        var top = rec.cardEl.getBoundingClientRect().top + window.pageYOffset - self.scrollOffset();
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      });
    }
    track('calculator_open', { calculator_name: rec.calc.name });
  };
  Inst.prototype.closeCard = function (id) {
    var rec = this.cards[id]; if (!rec) return;
    this.stash(id);
    rec.cardEl.classList.remove('is-open');
    rec.head.setAttribute('aria-expanded', 'false');
    rec.body.innerHTML = '';
    if (this.openId === id) this.openId = null;
  };
  Inst.prototype.stash = function (id) {
    var rec = this.cards[id];
    if (!rec || !rec.body.querySelector('[data-field]')) return;
    var vals = rec.state.vals || {};
    rec.body.querySelectorAll('[data-field]').forEach(function (inp) { vals[inp.dataset.field] = inp.value; });
    rec.state.vals = vals;
  };

  /* ---- build the open body ---- */
  Inst.prototype.buildBody = function (id) {
    var self = this, rec = this.cards[id], c = rec.calc, st = rec.state;
    if (!st.vals) { st.vals = {}; (c.fields || []).forEach(function (f) { st.vals[f.key] = f.default != null ? f.default : ''; }); }
    var s = makeUnits(this.units);
    var h = '';
    if (c.guide) h += '<div class="tabcalc-guide"><div class="tabcalc-guide-label">How it works</div><p>' + esc(c.guide) + '</p></div>';
    if (c.need) h += '<div class="tabcalc-need">' + ICON.need + '<p><b>What you’ll need: </b>' + esc(c.need) + '</p></div>';

    h += '<div class="tabcalc-fields">';
    (c.fields || []).forEach(function (f) {
      var unitLabel = f.dim === 'vol' ? s.volU : f.dim === 'temp' ? s.tempU : f.dim === 'rate' ? s.ratePerU : (f.unit || '');
      var val = st.vals[f.key] != null ? st.vals[f.key] : '';
      var fid = 'in-' + c.id + '-' + f.key;
      h += '<div class="tabcalc-field"><label for="' + fid + '">' + esc(f.label) + '</label>';
      if (f.type === 'select') {
        h += '<select id="' + fid + '" data-field="' + f.key + '">';
        (f.options || []).forEach(function (o) { h += '<option value="' + esc(o.value) + '"' + (String(val) === String(o.value) ? ' selected' : '') + '>' + esc(o.label) + '</option>'; });
        h += '</select>';
      } else {
        h += '<div class="tabcalc-inputwrap"><input id="' + fid + '" data-field="' + f.key + '" type="number" inputmode="decimal" step="' + esc(f.step || 'any') + '" value="' + esc(val) + '"' + (f.placeholder ? ' placeholder="' + esc(f.placeholder) + '"' : '') + '>' +
          (unitLabel ? '<span class="tabcalc-unit">' + esc(unitLabel) + '</span>' : '') + '</div>';
      }
      if (f.help) h += '<p class="tabcalc-help">' + esc(f.help) + '</p>';
      h += '</div>';
    });
    h += '</div>';

    h += '<div class="tabcalc-error" role="alert" aria-live="assertive" hidden></div>';
    h += '<button type="button" class="tabcalc-calc-btn">' + ICON.calc + 'Calculate</button>';
    h += '<div class="tabcalc-result"></div>';
    h += '<div class="tabcalc-cardactions"><button type="button" class="tabcalc-copy" hidden>' + ICON.copy + '<span>Copy result</span></button>' +
      '<button type="button" class="tabcalc-reset">Reset calculator</button></div>';
    h += '<span class="tabcalc-sr-live" aria-live="polite" style="position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0;"></span>';
    if (c.accuracy) h += '<div class="tabcalc-accuracy">' + ICON.info + '<p>' + esc(c.accuracy) + '</p></div>';
    if (c.caution) h += '<div class="tabcalc-caution">' + ICON.warn + '<div><div class="tabcalc-caution-head">Planning &amp; record keeping only</div><p>' + esc(c.caution) + '</p></div></div>';
    if (c.next && c.next.length) {
      h += '<div class="tabcalc-relatedwrap"><div class="tabcalc-related-label">Related</div><div class="tabcalc-related-chips">';
      c.next.forEach(function (nx) {
        var href = nx.t ? '#card-' + nx.t : (nx.h || '#');
        h += '<a class="tabcalc-related-chip" href="' + esc(href) + '"' + (nx.t ? ' data-jump="' + esc(nx.t) + '"' : '') + '>' + esc(nx.label) + ICON.arrow + '</a>';
      });
      h += '</div></div>';
    }
    h += '<details class="tabcalc-disc"><summary>' + ICON.info + 'Calculator disclaimer</summary><div class="tabcalc-disc-body">' +
      DISCLAIMER.map(function (p) { return '<p>' + esc(p) + '</p>'; }).join('') + '</div></details>';

    rec.body.innerHTML = h;

    // wire
    rec.body.querySelectorAll('[data-field]').forEach(function (inp) {
      var ev = inp.tagName === 'SELECT' ? 'change' : 'input';
      inp.addEventListener(ev, function () { st.vals[inp.dataset.field] = inp.value; st.copied = false; if (st.calculated) self.renderResult(id); });
      inp.addEventListener('keydown', function (e) { if (e.key === 'Enter' && inp.tagName !== 'SELECT') { e.preventDefault(); st.calculated = true; self.renderResult(id); } });
    });
    rec.body.querySelector('.tabcalc-calc-btn').addEventListener('click', function () { st.calculated = true; st.copied = false; self.renderResult(id); });
    rec.body.querySelector('.tabcalc-reset').addEventListener('click', function () { self.reset(id); });
    rec.body.querySelectorAll('.tabcalc-related-chip[data-jump]').forEach(function (a) {
      a.addEventListener('click', function (e) { e.preventDefault(); self.openCard(a.dataset.jump, true); });
    });
    rec.els = {
      error: rec.body.querySelector('.tabcalc-error'),
      result: rec.body.querySelector('.tabcalc-result'),
      copy: rec.body.querySelector('.tabcalc-copy'),
      srlive: rec.body.querySelector('.tabcalc-sr-live')
    };
    this.renderResult(id);
  };

  /* ---- compute + render result region (live) ---- */
  Inst.prototype.renderResult = function (id) {
    var rec = this.cards[id], c = rec.calc, st = rec.state, s = makeUnits(this.units);
    var vals = {};
    rec.body.querySelectorAll('[data-field]').forEach(function (inp) { vals[inp.dataset.field] = inp.value; });
    st.vals = vals;
    var out = null;
    if (st.calculated) { try { out = c.compute ? c.compute(vals, s) : null; } catch (e) { out = null; } }
    var isError = st.calculated && !!(out && out.error);
    var isResult = st.calculated && !!(out && out.main);

    // error block
    rec.els.error.hidden = !isError;
    rec.els.error.innerHTML = isError ? (ICON.error + '<p>' + esc(out.error) + '</p>') : '';

    if (isResult) {
      var summary = this.summary(c, vals, s);
      var extra = (out.extra || []).map(function (ex) {
        return '<div class="tabcalc-extra"><div class="tabcalc-extra-label">' + esc(ex.label) + '</div><div class="tabcalc-extra-val"><span>' + esc(ex.value) + '</span><span class="tabcalc-extra-unit">' + esc(ex.unit) + '</span></div></div>';
      }).join('');
      rec.els.result.innerHTML =
        '<div class="tabcalc-res-label">' + esc(out.main.label || 'Result') + '</div>' +
        '<div class="tabcalc-res-main"><span class="tabcalc-res-val">' + esc(out.main.value) + '</span><span class="tabcalc-res-unit">' + esc(out.main.unit) + '</span></div>' +
        (out.note ? '<p class="tabcalc-res-note">' + esc(out.note) + '</p>' : '') +
        (extra ? '<div class="tabcalc-extras">' + extra + '</div>' : '') +
        (summary ? '<div class="tabcalc-summary"><div class="tabcalc-summary-label">Your calculation</div><p>' + esc(summary) + '</p></div>' : '');
      rec.copyText = [
        c.name,
        out.main.value + ' ' + out.main.unit + (out.main.label ? '  (' + out.main.label + ')' : ''),
        out.note || '',
        (out.extra || []).map(function (e) { return e.label + ': ' + e.value + ' ' + e.unit; }).join('\n'),
        summary ? 'Your calculation: ' + summary : '',
        '— The Artisan’s Bottega'
      ].filter(Boolean).join('\n');
      rec.els.copy.hidden = false;
    } else {
      rec.els.result.innerHTML =
        '<div class="tabcalc-res-label">Your result</div>' +
        '<p class="tabcalc-res-empty">Enter your values above and press <b>Calculate</b>.</p>';
      rec.els.copy.hidden = true;
    }
    // copy handler (rebind fresh copyText)
    var self = this;
    rec.els.copy.onclick = function () { self.copy(id); };
    if (st.calculated && isResult) track('calculator_calculated', { calculator_name: c.name, result_returned: out.main.value + ' ' + out.main.unit });
  };

  Inst.prototype.summary = function (c, vals, s) {
    var parts = (c.fields || []).map(function (f) {
      var raw = vals[f.key];
      if (raw === '' || raw == null) return null;
      if (f.type === 'select') { var opt = (f.options || []).find(function (o) { return o.value === raw; }); return opt ? opt.label : null; }
      if (isNaN(parseFloat(raw))) return null;
      var u = f.dim === 'vol' ? s.volU : f.dim === 'temp' ? s.tempU : f.dim === 'rate' ? s.ratePerU : (f.unit || '');
      return f.label + ' ' + raw + (u ? ' ' + u : '');
    }).filter(Boolean);
    return parts.join('  ·  ');
  };

  /* ---- reset ---- */
  Inst.prototype.reset = function (id) {
    var rec = this.cards[id];
    rec.state.vals = {}; (rec.calc.fields || []).forEach(function (f) { rec.state.vals[f.key] = ''; });
    rec.state.calculated = false; rec.state.copied = false;
    this.buildBody(id);
  };

  /* ---- copy ---- */
  Inst.prototype.copy = function (id) {
    var rec = this.cards[id]; if (!rec || !rec.copyText) return;
    var label = rec.els.copy.querySelector('span');
    var done = function () { if (rec.els.srlive) rec.els.srlive.textContent = 'Result copied to clipboard'; if (label) { var old = 'Copy result'; label.textContent = 'Copied to clipboard ✓'; rec.els.copy.classList.add('is-copied'); setTimeout(function () { label.textContent = old; rec.els.copy.classList.remove('is-copied'); }, 1800); } };
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(rec.copyText).then(done, function () { legacy(rec.copyText); done(); });
      else { legacy(rec.copyText); done(); }
    } catch (e) { legacy(rec.copyText); done(); }
    track('calculator_copy_result', { calculator_name: rec.calc.name });
  };
  function legacy(text) { var ta = document.createElement('textarea'); ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0'; document.body.appendChild(ta); ta.select(); try { document.execCommand('copy'); } catch (e) {} document.body.removeChild(ta); }

  /* ---- public API ---- */
  var TABCalc = {
    register: function (key, cfg) { registry[key] = cfg; },
    boot: function () {
      document.querySelectorAll('.tabcalc-root[data-page]').forEach(function (root) {
        var cfg = registry[root.getAttribute('data-page')];
        if (!cfg) return;
        var inst = new Inst(root, cfg);
        root._tabcalc = inst;
        inst.init();
      });
    }
  };
  window.TABCalc = TABCalc;
  if (window.TABCalc._pending) { window.TABCalc._pending.forEach(function (p) { registry[p[0]] = p[1]; }); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', TABCalc.boot);
  else TABCalc.boot();
})();
